import type { StagePositions } from './api.ts'
import { ErpCodeVerdict, stageOf, type ErpCodeColumn } from './vocabulary.ts'

/**
 * What the ERP codes check answers (#672), and the two things the account
 * page works out from it. Mirrors the route's response.
 */

/** One value a code column holds on the account's installation. */
export interface ErpCodeFound {
  /** As the ERP keeps it, trimmed; `''` for an empty column. */
  code: string
  count: number
  verdict: ErpCodeVerdict
  /** What our tables read it as, in the screen's words; null when they read nothing. */
  reading: string | null
}

export interface ErpCodeColumnCount {
  column: ErpCodeColumn
  /** The ERP's own name for the column, `FC12100.TPFORMAFARMA`. */
  source: string
  /** Why this column could not be counted; the others stand. */
  error: string | null
  /** Most frequent first. */
  codes: ErpCodeFound[]
}

/** One row of `FC12540`, as the installation typed it. */
export interface ErpStageRow {
  code: string
  position: number
  name: string
}

export type ErpCodesCheckResult =
  | {
      ok: true
      ms: number
      /** How far back the requisitions were counted, in days. */
      windowDays: number
      columns: ErpCodeColumnCount[]
      /** Null when the ERP would not give the list; `stagesError` says why. */
      stages: ErpStageRow[] | null
      stagesError: string | null
      /** Where the account's stages start on its FC12540 (#673), which the stage list is read on. */
      stagePositions: StagePositions
    }
  | { ok: false; ms: number; error: string }

/**
 * The sentence over the tables, and whether it warns: the values no table
 * knows, and anything the ERP would not count. A column it would not count
 * has no values at all, so "nothing unknown" is said only when every column
 * and the stage list came back.
 */
export function checkHeadline(check: {
  readonly columns: readonly ErpCodeColumnCount[]
  readonly stagesError: string | null
}): { readonly text: string; readonly warn: boolean } {
  const unknown = check.columns.reduce(
    (sum, one) =>
      sum + one.codes.filter((found) => found.verdict === ErpCodeVerdict.Unknown).length,
    0,
  )
  const uncounted = check.columns.filter((one) => one.error !== null).length
  if (unknown === 0 && uncounted === 0 && check.stagesError === null) {
    return { text: 'Nenhum valor desconhecido.', warn: false }
  }
  const parts = [
    unknown === 0
      ? 'Nenhum valor desconhecido nas colunas contadas.'
      : `${unknown} ${unknown === 1 ? 'valor desconhecido' : 'valores desconhecidos'}, em destaque.`,
  ]
  if (uncounted > 0) {
    parts.push(
      `${uncounted} ${uncounted === 1 ? 'coluna não pôde ser contada' : 'colunas não puderam ser contadas'}.`,
    )
  }
  if (check.stagesError !== null) parts.push('As etapas não puderam ser lidas.')
  return { text: parts.join(' '), warn: true }
}

/**
 * Whether a stage sits where the account's queue reads verification, on its
 * own positions (#673). Without one, no requisition of this installation would
 * ever be in verification.
 */
export function hasVerificationStage(
  stages: readonly ErpStageRow[],
  positions: StagePositions,
): boolean {
  return stages.some((stage) => stageOf(stage.position, positions) === 'conferencia')
}
